import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bell, BellOff, Briefcase, Leaf, FilePlus, FileUp, Headphones, RefreshCw, Search, Settings, ShoppingCart, Users } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import CallRow from '../../components/sales/CallRow';
import VoicemailCard from '../../components/sales/VoicemailCard';
import FollowUpsBoard from '../../components/sales/FollowUpsBoard';
import salesService from '../../services/sales';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone } from '../../utils/timezone';
import { MATCH_KIND_LABEL, isOverdue } from '../../utils/calls';
import {
  desktopNotifyState, turnOffDesktopNotify, turnOnDesktopNotify, type DesktopNotifyState,
} from '../../lib/desktopNotify';
import type { Call, CallerLookup, PhoneDomain, SalesPerson, Voicemail, VoicemailFilter } from '../../types/sales';

/**
 * /sales — the Sales module's home (docs/sales-module-plan.md).
 *
 * Voicemails first (`?tab=voicemails`, the default): waiting, mine, or all,
 * each with its transcript and audio for those who may hear it, and who it
 * is given to until it is followed up. Follow-ups (`?tab=follow-ups`): mine,
 * everyone's open, or done, overdue first; one can be made from a voicemail
 * or a call, or here. Then the call log (`?tab=calls`), a recorded call
 * opening to its recording and transcript. "Whose number is this?" finds a
 * number among the people on file, and desktop notifications for incoming
 * calls are switched on here, per browser.
 */

type Tab = 'voicemails' | 'follow-ups' | 'calls';
const TABS: Tab[] = ['voicemails', 'follow-ups', 'calls'];

/** Selling is the Sales module's (Rob, 2026-10-19): the pages it opens. */
const SELLING = [
  { label: 'New order', path: '/nursery/orders/new', icon: FilePlus },
  { label: 'Orders', path: '/nursery/orders', icon: ShoppingCart },
  { label: 'Customers', path: '/accounts/customers', icon: Users },
  { label: 'Cultivars', path: '/cultivar', icon: Leaf },
  { label: 'Trade account requests', path: '/nursery/trade-account-requests', icon: Briefcase },
  { label: 'Import purchase orders', path: '/nursery/orders/import', icon: FileUp },
];

const FILTERS: { key: VoicemailFilter; label: string }[] = [
  { key: 'open', label: 'Waiting' },
  { key: 'mine', label: 'Mine' },
  { key: 'all', label: 'All' },
];

function NotifyToggle() {
  const [state, setState] = useState<DesktopNotifyState>(() => desktopNotifyState());
  if (state === 'unsupported') return null;
  if (state === 'blocked') {
    return (
      <p className="text-xs text-neutral-500 break-words">
        <BellOff className="h-4 w-4 inline mr-1" aria-hidden />
        This browser blocks notifications from FactreeTech; allow them in its site settings to be told about calls.
      </p>
    );
  }
  return state === 'on' ? (
    <Button size="sm" variant="ghost" onClick={() => setState(turnOffDesktopNotify())}>
      <BellOff className="h-4 w-4 mr-1" aria-hidden />Turn off desktop notifications
    </Button>
  ) : (
    <Button size="sm" variant="outline" onClick={async () => setState(await turnOnDesktopNotify())}>
      <Bell className="h-4 w-4 mr-1" aria-hidden />Turn on desktop notifications
    </Button>
  );
}

function SalesHome() {
  const tz = useOrgTimezone();
  const { hasAccess: isAdmin } = useModuleLicense('sales', 'admin');
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.find((t) => t === params.get('tab')) ?? 'voicemails';
  const filter: VoicemailFilter = (tab === 'voicemails' && (['open', 'mine', 'all'] as const).find((f) => f === params.get('show'))) || 'open';

  const [calls, setCalls] = useState<Call[] | null>(null);
  const [voicemails, setVoicemails] = useState<Voicemail[] | null>(null);
  const [people, setPeople] = useState<SalesPerson[]>([]);
  const [domains, setDomains] = useState<PhoneDomain[] | null>(null);
  const [mineOpen, setMineOpen] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [number, setNumber] = useState('');
  const [lookup, setLookup] = useState<CallerLookup | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [looking, setLooking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, v, d] = await Promise.all([
        salesService.listCalls({ limit: 50 }),
        salesService.listVoicemails(filter),
        salesService.listDomains(),
      ]);
      if (!c.success) throw c;
      if (!v.success) throw v;
      setCalls(c.data);
      setVoicemails(v.data);
      setDomains(d.success ? d.data : []);
      setError(null);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the calls'));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // The count of mine on the tab; the board itself keeps it true while it is open.
  const countMine = useCallback(() => {
    salesService.listFollowUps('mine').then((r) => { if (r.success) setMineOpen((r.data ?? []).length); }).catch(() => {});
  }, []);
  useEffect(() => { countMine(); }, [countMine]);
  useEffect(() => {
    salesService.listSalesPeople().then((r) => { if (r.success) setPeople(r.data); }).catch(() => {});
  }, []);

  const setTab = (next: Tab) => {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    p.delete('show');
    setParams(p, { replace: true });
  };
  const setFilter = (next: VoicemailFilter) => {
    const p = new URLSearchParams(params);
    p.set('tab', 'voicemails');
    p.set('show', next);
    setParams(p, { replace: true });
  };
  const followUpChanged = countMine;

  const findNumber = async (e: FormEvent) => {
    e.preventDefault();
    if (!number.trim()) return;
    setLooking(true);
    setLookupError(null);
    try {
      const res = await salesService.lookup(number.trim());
      if (!res.success) throw res;
      setLookup(res.data);
    } catch (err) {
      setLookupError(errorMessage(err, 'Could not look up that number'));
    } finally {
      setLooking(false);
    }
  };

  const replaceVoicemail = (vm: Voicemail) => {
    setVoicemails((list) => {
      if (!list) return list;
      const kept = filter === 'all' || vm.status !== 'followed_up';
      return kept ? list.map((x) => (x.id === vm.id ? vm : x)) : list.filter((x) => x.id !== vm.id);
    });
  };

  const noPhone = domains !== null && domains.length === 0;
  const waiting = voicemails?.filter((v) => v.status !== 'followed_up').length ?? 0;
  const overdue = voicemails?.filter((v) => isOverdue(v)).length ?? 0;

  const tabCls = (on: boolean) => `px-3 py-2 text-sm font-medium rounded-lg ${on ? 'bg-primary-100 text-primary-800' : 'text-neutral-600 hover:bg-neutral-100'}`;

  return (
    <>
      <PageHeader
        title="Sales"
        subtitle="Customers, orders, and the calls and voicemails against them"
        icon={<Headphones className="h-6 w-6 text-white" />}
        actionButtons={isAdmin ? (
          <Link to="/sales/phone">
            <Button variant="outline" size="sm"><Settings className="h-4 w-4 mr-1" aria-hidden />Phone system</Button>
          </Link>
        ) : undefined}
      />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {noPhone && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 break-words">
            No phone system is connected yet.{' '}
            {isAdmin
              ? <Link to="/sales/phone" className="font-medium underline">Connect one</Link>
              : 'A Sales Admin connects it.'}
          </div>
        )}

        <nav aria-label="Selling" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {SELLING.map(({ label, path, icon: Icon }) => (
            <Link key={path} to={path}
              className="flex items-center gap-2 p-3 bg-white border border-neutral-200 rounded-xl hover:border-primary-300 hover:shadow-sm text-sm font-medium text-neutral-800 min-w-0">
              <Icon className="h-5 w-5 text-primary-600 shrink-0" aria-hidden />
              <span className="break-words min-w-0">{label}</span>
            </Link>
          ))}
        </nav>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1" role="tablist" aria-label="Sales">
            <button type="button" role="tab" aria-selected={tab === 'voicemails'} className={tabCls(tab === 'voicemails')} onClick={() => setTab('voicemails')}>
              Voicemails{waiting && filter !== 'all' ? ` (${waiting})` : ''}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'follow-ups'} className={tabCls(tab === 'follow-ups')} onClick={() => setTab('follow-ups')}>
              Follow-ups{mineOpen ? ` (${mineOpen})` : ''}
            </button>
            <button type="button" role="tab" aria-selected={tab === 'calls'} className={tabCls(tab === 'calls')} onClick={() => setTab('calls')}>
              Calls
            </button>
          </div>
          <div className="flex items-center gap-2">
            <NotifyToggle />
            <Button variant="ghost" size="sm" onClick={() => { load(); countMine(); }} isLoading={loading} aria-label="Refresh">
              <RefreshCw className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>

        {error && <p className="text-sm text-red-700 break-words">{error}</p>}

        {tab === 'voicemails' ? (
          <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b border-neutral-100">
              <h2 className="text-base font-semibold text-neutral-900">
                Voicemails
                {overdue > 0 && <span className="ml-2 text-sm font-normal text-amber-800">{overdue} waiting over a day</span>}
              </h2>
              <div className="flex gap-1">
                {FILTERS.map((f) => (
                  <button key={f.key} type="button" onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}
                    className={`px-2 py-1 text-xs font-medium rounded-md ${filter === f.key ? 'bg-neutral-800 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            {voicemails && voicemails.length === 0 && (
              <p className="p-4 text-sm text-neutral-600">
                {filter === 'all' ? 'No voicemails yet.' : filter === 'mine' ? 'Nothing waiting for you.' : 'Nothing waiting to be followed up.'}
              </p>
            )}
            {voicemails && voicemails.length > 0 && (
              <ul className="divide-y divide-neutral-100">
                {voicemails.map((v) => (
                  <VoicemailCard key={v.id} vm={v} people={people} tz={tz} onChange={replaceVoicemail} onFollowUp={followUpChanged} />
                ))}
              </ul>
            )}
          </section>
        ) : tab === 'follow-ups' ? (
          <FollowUpsBoard tz={tz} onMineCount={setMineOpen} />
        ) : (
          <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
            <h2 className="p-4 border-b border-neutral-100 text-base font-semibold text-neutral-900">Recent calls</h2>
            {calls && calls.length === 0 && (
              <p className="p-4 text-sm text-neutral-600">No calls yet. They appear here once the phone system's bridge is sending them.</p>
            )}
            {calls && calls.length > 0 && (
              <ul className="divide-y divide-neutral-100">
                {calls.map((c) => <CallRow key={c.id} call={c} tz={tz} people={people} onFollowUp={followUpChanged} />)}
              </ul>
            )}
          </section>
        )}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3 min-w-0">
          <h2 className="text-base font-semibold text-neutral-900">Whose number is this?</h2>
          <form onSubmit={findNumber} className="flex flex-col sm:flex-row gap-2">
            <label htmlFor="sales-lookup" className="sr-only">Phone number</label>
            <input
              id="sales-lookup"
              type="tel"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="0412 345 678"
              className="w-full sm:w-64 rounded-lg border border-neutral-300 px-3 py-2 text-sm min-w-0"
            />
            <Button type="submit" size="sm" isLoading={looking}><Search className="h-4 w-4 mr-1" aria-hidden />Look up</Button>
          </form>
          {lookupError && <p className="text-sm text-red-700 break-words">{lookupError}</p>}
          {lookup && (
            lookup.e164 === null ? (
              <p className="text-sm text-neutral-600">That does not read as a phone number. Include the area code.</p>
            ) : lookup.matches.length === 0 ? (
              <p className="text-sm text-neutral-600">Nobody on file has {lookup.e164}.</p>
            ) : (
              <ul className="text-sm divide-y divide-neutral-100">
                {lookup.matches.map((m) => (
                  <li key={`${m.kind}-${m.id}`} className="py-2 break-words">
                    {m.customer_id
                      ? <Link to={`/accounts/customers/${m.customer_id}`} className="font-medium text-primary-700 hover:underline">{m.name}</Link>
                      : <span className="font-medium">{m.name}</span>}
                    <span className="text-neutral-500"> · {MATCH_KIND_LABEL[m.kind]}</span>
                    {m.kind !== 'customer' && m.customer_name && m.customer_name !== m.name && (
                      <span className="text-neutral-500"> of {m.customer_name}</span>
                    )}
                  </li>
                ))}
              </ul>
            )
          )}
        </section>
      </div>
    </>
  );
}

export default SalesHome;
